
import { useRef } from "react";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { SubmitFinalData, jumpToStep, nextStep, previousStep } from "../features/FormNavigator/navigatorSlice";
import { SubmitManagerDetails } from "../features/1_ManagerDetails/managerDetailsSlice";
import { SubmitLicenseStates } from "../features/3_LicenseStates/licenseStatesSlice";
import { SubmitBranchLocationDetails, SubmitUtilities } from "../features/4_5_6_BranchLocationDetails/branchLocationDetailsSlice";
import { SubmitLockingCabinetDetails } from "../features/7_LockingCabinet/lockingCabinetSlice";
import { SubmitLocalITCompanyDetails } from "../features/8_localITCompanyDetails/localITCompanySlice";
import { SubmitMortgageSign } from "../features/10_mortgageSign/mortgageSignSlice";
import { SubmitComplianceSignage } from "../features/11_ComplianceSignage/complianceSignageSlice";
import { SubmitOccupationalLicense } from "../features/12_occupationalLicence/occupationalLicenseSlice";
import { SubmitOfficeSpaceOtherBusinessDetails } from "../features/9_officeSpaceOtherBusinessDetails/officeSpaceOtherBusinessSlice";
import { SubmitMarginManagement } from "../features/3.0_MarginManagement/marginManagementSlice";
import { SubmitProcessingPlan } from "../features/3.1_ProcessingPlan/processingPlanSlice";
import { SubmitMarketingDetails } from "../features/12.1_MarketingDetails/marketingDetailsSlice";
import { requestErrorShownSince } from "../errors/errorHandling";

const Navigation = () => {
    const navigatorState = useSelector(state => state.navigator);
    const managerDetailsState = useSelector(state => state.manager);
    const marketingDetailsState = useSelector(state => state.marketing);
    const marginManagementState = useSelector(state => state.margin_management);
    const processingPlanState = useSelector(state => state.processing_plan);
    const licenseStatesState = useSelector(state => state.license_states);
    const branchLocationDetailsState = useSelector(state => state.branch_location);
    const lockingCabinetDetailsState = useSelector(state => state.locking_cabinet);
    const localITCompanyDetailsState = useSelector(state => state.local_it_company);
    const mortgageSignState = useSelector(state => state.mortgage_sign);
    const complianceSignageState = useSelector(state => state.compliance_signage);
    const occupationalLicenseState = useSelector(state => state.occupational_license);
    const officeSpaceOtherBusinessDetailsState = useSelector(state => state.other_businesses);

    const dispatch = useDispatch();
    // // console.log(managerDetailsState, navigatorState);

    // Show a styled toast instead of the browser's native alert()
    // Set when Next / Finish is pressed. A request that failed after that was
    // already toasted by the global fetch observer — only the form's own
    // validation messages still need one here.
    const actionStartedAt = useRef(0);
    const showError = (err) => {
        if (requestErrorShownSince(actionStartedAt.current)) return;
        const message = typeof err === 'string'
            ? err
            : (err?.message || err?.error || 'Something went wrong. Please try again.');
        toast.error(message);
    };
    
    // skipping steps based selected option
    // also track previous step if skipped
    const handleNext = (e, jumpToSummary = false) => {
        actionStartedAt.current = Date.now();
        switch (navigatorState.currentStep) {
        case 1:
            dispatch(SubmitManagerDetails(managerDetailsState.data)).unwrap()
                        .then(() => {
                            jumpToSummary ? dispatch(jumpToStep({ step: navigatorState.maxSteps })) : dispatch(nextStep())
                        }).catch(err => showError(err))
            break
        case 2:
            jumpToSummary ? dispatch(jumpToStep({ step: navigatorState.maxSteps })) : dispatch(nextStep())
            break
        case 3:
            dispatch(SubmitLicenseStates(licenseStatesState.data)).unwrap()
                        .then(() => {
                            jumpToSummary ? dispatch(jumpToStep({ step: navigatorState.maxSteps })) : dispatch(nextStep())
                        }).catch(err => showError(err))
            break;
        // case 4: reserved for future client use — Branch Location Type (Standalone / Satellite selector)
        //     dispatch(SubmitBranchLocationDetails(branchLocationDetailsState.data)).unwrap()
        //                 .then(() => {
        //                     jumpToSummary ? dispatch(jumpToStep({ step: navigatorState.maxSteps })) : dispatch(nextStep())
        //                 }).catch(err => showError(err))
        //     break
        case 5:
            dispatch(SubmitProcessingPlan(processingPlanState.data)).unwrap()
                        .then(() => {
                            jumpToSummary ? dispatch(jumpToStep({ step: navigatorState.maxSteps })) : dispatch(nextStep())
                        }).catch(err => showError(err))
            break;
        case 6:
            dispatch(SubmitMarginManagement(marginManagementState.data)).unwrap()
                        .then(() => {
                            jumpToSummary ? dispatch(jumpToStep({ step: navigatorState.maxSteps })) : dispatch(nextStep())
                        }).catch(err => showError(err))
            break;
        case 7:
            dispatch(SubmitBranchLocationDetails(branchLocationDetailsState.data))
                        .unwrap()
                        .then(() => {
                            // console.log("Called: ", branchLocationDetailsState.data);
                            if (branchLocationDetailsState.data._option === 'Plan on getting location'
                                || branchLocationDetailsState.data._option === 'No location / virtual location')
                                {
                                    dispatch(jumpToStep({ step: navigatorState.maxSteps - 1 })) // skipping to Marketing
                                }
                            else {
                                dispatch(nextStep())
                            }
                        })
                        .catch(err => showError(err))
            break;
        case 8:
            dispatch(SubmitBranchLocationDetails(branchLocationDetailsState.data)).unwrap()
                        .then(() => {
                            jumpToSummary ? dispatch(jumpToStep({ step: navigatorState.maxSteps })) : dispatch(nextStep())
                        }).catch(err => showError(err))
            break;
        case 9:
            dispatch(SubmitUtilities(branchLocationDetailsState.data.utilities)).unwrap()
                        .then(() => {
                            jumpToSummary ? dispatch(jumpToStep({ step: navigatorState.maxSteps })) : dispatch(nextStep())
                        }).catch(err => showError(err))
            break;
        case 10:
            dispatch(SubmitLockingCabinetDetails(lockingCabinetDetailsState.data)).unwrap()
                        .then(() => {
                            jumpToSummary ? dispatch(jumpToStep({ step: navigatorState.maxSteps })) : dispatch(nextStep())
                        }).catch(err => showError(err))
            break;
        case 11:
            dispatch(SubmitLockingCabinetDetails(lockingCabinetDetailsState.data)).unwrap()
                        .then(() => {
                            jumpToSummary ? dispatch(jumpToStep({ step: navigatorState.maxSteps })) : dispatch(nextStep())
                        }).catch(err => showError(err))
            break;
        case 12:
            dispatch(SubmitLocalITCompanyDetails(localITCompanyDetailsState.data)).unwrap()
                        .then(() => {
                            jumpToSummary ? dispatch(jumpToStep({ step: navigatorState.maxSteps })) : dispatch(nextStep())
                        }).catch(err => showError(err))
            break;
        case 13:
            dispatch(SubmitOfficeSpaceOtherBusinessDetails(officeSpaceOtherBusinessDetailsState.data)).unwrap()
                        .then(() => {
                            jumpToSummary ? dispatch(jumpToStep({ step: navigatorState.maxSteps })) : dispatch(nextStep())
                        }).catch(err => showError(err))
            break;
        case 14:
            dispatch(SubmitOfficeSpaceOtherBusinessDetails(officeSpaceOtherBusinessDetailsState.data)).unwrap()
                        .then(() => {
                            jumpToSummary ? dispatch(jumpToStep({ step: navigatorState.maxSteps })) : dispatch(nextStep())
                        }).catch(err => showError(err))
            break;
        case 15:
            dispatch(SubmitMortgageSign(mortgageSignState.data)).unwrap()
                        .then(() => {
                            jumpToSummary ? dispatch(jumpToStep({ step: navigatorState.maxSteps })) : dispatch(nextStep())
                        }).catch(err => showError(err))
            break;
        case 16:
            dispatch(SubmitComplianceSignage({data: complianceSignageState.data})).unwrap()
                        .then(() => {
                            jumpToSummary ? dispatch(jumpToStep({ step: navigatorState.maxSteps })) : dispatch(nextStep())
                        }).catch(err => showError(err))
            break;
        case 17:
            dispatch(SubmitOccupationalLicense(occupationalLicenseState.data)).unwrap()
                        .then(() => {
                            jumpToSummary ? dispatch(jumpToStep({ step: navigatorState.maxSteps })) : dispatch(nextStep())
                        }).catch(err => showError(err))
            break;
        case 18:
            dispatch(SubmitMarketingDetails(marketingDetailsState.data)).unwrap()
                        .then(() => {
                            jumpToSummary ? dispatch(jumpToStep({ step: navigatorState.maxSteps })) : dispatch(nextStep())
                        }).catch(err => showError(err))
            break
        default:
            dispatch(nextStep())
            break;
        }
    }


    const handleFinishButton = (e) => {
        e.preventDefault();
        actionStartedAt.current = Date.now();
        dispatch(SubmitFinalData({}))
                    .unwrap()
                    .then(response => {
                        console.log(response);
                        localStorage.removeItem('navigatorState');
                        window.location.href = '/success';
                    })
                    .catch(err => showError("Failed to submit form."))

    }

    return (
        <div className="flex w-full flex-col-reverse items-stretch justify-center gap-3 sm:flex-row sm:items-center sm:gap-4">
          {/* Previous Button */}
          { (navigatorState.currentStep > 1 && navigatorState.currentStep < navigatorState.maxSteps) && 
            <button className="inline-flex h-12 w-full items-center justify-center rounded-xl border border-slate-300 bg-white px-5 text-sm font-semibold text-[#211b4f] transition-colors hover:bg-slate-50 sm:w-auto sm:min-w-[9rem]"
                    onClick={() => dispatch(previousStep())}>
                <svg className="mr-2 h-4 w-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10.293 15.707a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 111.414 1.414L6.414 10l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                </svg>
                Previous
            </button>
          }

          {/* Next Button */}
          {
            navigatorState.currentStep < navigatorState.maxSteps &&
            <button className="inline-flex h-12 w-full items-center justify-center rounded-xl bg-[#211b4f] px-6 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#30266c] sm:w-auto sm:min-w-[9rem]"
                onClick={handleNext}>
                Next
                <svg className="ml-2 h-4 w-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10.293 4.293a1 1 0 011.414 0l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414-1.414L13.586 10l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
            </button>
          }

          {/* Skip to Summary Button */}
            {
            navigatorState.currentStep !== navigatorState.maxSteps &&
            navigatorState.visitedSteps.includes(navigatorState.maxSteps) &&
            // ![6,7,8,9,10,11,12,13,14,15,16].includes(navigatorState.currentStep) && // because modifying branch location will affect slides
            <button className="inline-flex h-12 w-full items-center justify-center rounded-xl border border-[#fb6635] bg-white px-5 text-sm font-semibold text-[#fb6635] transition-colors hover:bg-orange-50 sm:w-auto sm:min-w-[9rem]"
                onClick={(e) => handleNext(e, true)}>
                To Summary
                <svg className="ml-2 h-4 w-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10.293 4.293a1 1 0 011.414 0l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414-1.414L13.586 10l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
            </button>
          }

           {/* Finish Button */}
            { navigatorState.currentStep === navigatorState.maxSteps && 
                <button className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#fb6635] px-6 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#e95729] sm:w-auto sm:min-w-[9rem]"
                        onClick={handleFinishButton}
                    >
                        <svg className="h-4 w-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor">
                            <path d="M12.354 4.354a.5.5 0 0 0-.708-.708L5 10.293 1.854 7.146a.5.5 0 1 0-.708.708l3.5 3.5a.5.5 0 0 0 .708 0zm-4.208 7-.896-.897.707-.707.543.543 6.646-6.647a.5.5 0 0 1 .708.708l-7 7a.5.5 0 0 1-.708 0"/>
                            <path d="m5.354 7.146.896.897-.707.707-.897-.896a.5.5 0 1 1 .708-.708"/>
                        </svg>
                        Finish
                </button>
            }
        </div>
    )
};

export default Navigation;